// Blueprint: crap/blueprint/workspace/architecture/001-coalesce-workspace-events.md
import DiagnosticsModule from '@modules/DiagnosticsModule';
import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  CoalesceFlushReason,
  CoalesceTimerArmed,
  RTCSMetricNames,
  RTCSMetricTags,
} from '../../metrics/rtcsMetrics';
import { logException } from './log';
import isValidUser from './isValidUser';
import requireUser from './requireUser';
import { serviceState } from './state';
import type ServiceUser from '../types/ServiceUser';

// Non-self agent.session* (CCPE-2281) share this buffer with agent.status
// only when the workspace flag is on. Session lifecycle is FIFO; status is
// last-write-wins per agentId. Flush prepares in parallel then commits
// synchronously in enqueue order (one valtio notify).
const FLUSH_DELAY_MS = 1000;

export type CoalescedApply<T = unknown> = {
  prepare: () => Promise<T | undefined>;
  commit: (value: T) => void;
  persistSession?: () => void;
};

type SessionItem = {
  kind: 'session';
  agentId: string;
  prepare: () => Promise<unknown>;
  commit: (value: unknown) => void;
  persistSession?: () => void;
};

type AgentStatusItem = {
  kind: 'agentStatus';
  agentId: string;
  generation: number;
  prepare: () => Promise<unknown>;
  commit: (value: unknown) => void;
};

type PendingItem = SessionItem | AgentStatusItem;

const pending: PendingItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const inFlightFlushes = new Set<Promise<void>>();
const kickedBatches = new Set<PendingItem[]>();
const agentStatusGeneration = new Map<string, number>();
let flushChain: Promise<void> = Promise.resolve();

function armFlushTimer() {
  flushTimer ??= setTimeout(() => {
    kickFlush();
  }, FLUSH_DELAY_MS);
}

function peekCoalescedAgentStatusGeneration(agentId: string): number {
  return agentStatusGeneration.get(agentId) ?? 0;
}

function isCoalescedAgentStatusInvalidated(
  agentId: string,
  generation: number,
): boolean {
  return peekCoalescedAgentStatusGeneration(agentId) !== generation;
}

function invalidateCoalescedAgentStatus(agentId: string) {
  agentStatusGeneration.set(
    agentId,
    peekCoalescedAgentStatusGeneration(agentId) + 1,
  );
}

function wrapStatusApply<T>(
  agentId: string,
  steps: CoalescedApply<T>,
): Pick<AgentStatusItem, 'generation' | 'prepare' | 'commit'> {
  const generation = peekCoalescedAgentStatusGeneration(agentId);
  return {
    generation,
    prepare: async () => {
      if (isCoalescedAgentStatusInvalidated(agentId, generation)) {
        return undefined;
      }
      return steps.prepare();
    },
    commit: (value: unknown) => {
      if (value === undefined) {
        return;
      }
      if (isCoalescedAgentStatusInvalidated(agentId, generation)) {
        return;
      }
      steps.commit(value as T);
    },
  };
}

function wrapSessionApply<T>(
  steps: CoalescedApply<T>,
): Pick<SessionItem, 'prepare' | 'commit' | 'persistSession'> {
  return {
    prepare: () => steps.prepare(),
    commit: (value: unknown) => {
      if (value === undefined) {
        return;
      }
      steps.commit(value as T);
    },
    persistSession: steps.persistSession,
  };
}

function toSteps<T>(
  apply: (() => Promise<void>) | CoalescedApply<T>,
): CoalescedApply<T> {
  if (typeof apply === 'function') {
    return {
      prepare: async () => {
        await apply();
        return undefined;
      },
      commit: () => {},
    };
  }
  return apply;
}

function applyImmediate<T>(
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (typeof apply === 'function') {
    return apply();
  }
  return apply.prepare().then((prepared) => {
    if (prepared !== undefined) {
      apply.commit(prepared);
    }
  });
}

/**
 * Drops the buffered status for one agent on this coalescer (2281-on path).
 * Session enqueue also drops: a pre-session snapshot is stale.
 * Callers that must also clear the CCPE-1920 Map should use
 * `handleAgentStatus.dropPendingAgentStatus`.
 */
export function dropCoalescedPendingAgentStatus(agentId: string) {
  dropPendingByKind(agentId, 'agentStatus');
  invalidateCoalescedAgentStatus(agentId);
}

function dropPendingByKind(agentId: string, kind: PendingItem['kind']) {
  for (let i = pending.length - 1; i >= 0; i--) {
    const item = pending[i];
    if (item.kind === kind && item.agentId === agentId) {
      pending.splice(i, 1);
    }
  }
}

function takeSessionsForAgent(
  items: PendingItem[],
  agentId: string,
): SessionItem[] {
  const taken: SessionItem[] = [];
  for (let i = 0; i < items.length; ) {
    const item = items[i];
    if (item.kind === 'session' && item.agentId === agentId) {
      taken.push(item);
      items.splice(i, 1);
    } else {
      i++;
    }
  }
  return taken;
}

function recordCoalesceFlush(
  reason: (typeof CoalesceFlushReason)[keyof typeof CoalesceFlushReason],
  batchSize: number,
  timerArmed?: boolean,
) {
  try {
    const tags: Record<string, string> = {
      [RTCSMetricTags.COALESCE_FLUSH_REASON]: reason,
    };
    if (reason === CoalesceFlushReason.DRAIN && timerArmed != null) {
      tags[RTCSMetricTags.COALESCE_TIMER_ARMED] = timerArmed
        ? CoalesceTimerArmed.TRUE
        : CoalesceTimerArmed.FALSE;
    }
    DiagnosticsModule.incrementCounter(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_FLUSH_TOTAL,
      tags,
    );
    DiagnosticsModule.recordHistogram(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_FLUSH_SIZE,
      batchSize,
      tags,
    );
  } catch {
    // Metrics must not break flush.
  }
}

function recordCoalesceDrainWait(waitMs: number) {
  try {
    DiagnosticsModule.recordHistogram(
      RTCSMetricNames.RTCS_WORKSPACE_COALESCE_DRAIN_WAIT_DURATION,
      waitMs,
    );
  } catch {
    // Metrics must not break drain.
  }
}

function persistBufferedSessionStores() {
  for (const item of pending) {
    if (item.kind !== 'session' || !item.persistSession) {
      continue;
    }
    try {
      item.persistSession();
    } catch (err) {
      logException(err, 'rtcs:agent:session:persist');
    }
  }
}

function clearPendingBuffer() {
  if (flushTimer) {
    clearTimeout(flushTimer);
  }
  flushTimer = null;
  pending.length = 0;
}

export async function resetPendingWorkspaceEvents(): Promise<void> {
  persistBufferedSessionStores();
  clearPendingBuffer();
  await Promise.allSettled([...inFlightFlushes]);
  persistBufferedSessionStores();
  clearPendingBuffer();
}

function isStatusItem(item: PendingItem): item is AgentStatusItem {
  return item.kind === 'agentStatus';
}

function flushLogContext(item: PendingItem) {
  return item.kind === 'session'
    ? 'rtcs:agent:session:flush'
    : 'rtcs:agent:status:flush';
}

function shouldDropStatusItem(item: PendingItem): boolean {
  return (
    isStatusItem(item) &&
    (item.agentId === serviceState.contactCenterUserId ||
      isCoalescedAgentStatusInvalidated(item.agentId, item.generation))
  );
}

async function prepareFlushSlot(item: PendingItem) {
  try {
    if (shouldDropStatusItem(item)) {
      return null;
    }
    const prepared = await item.prepare();
    return { item, prepared };
  } catch (err) {
    logException(err, flushLogContext(item));
    return null;
  }
}

function commitFlushSlot(
  slot: { item: PendingItem; prepared: unknown } | null,
) {
  if (!slot || shouldDropStatusItem(slot.item)) {
    return;
  }
  try {
    slot.item.commit(slot.prepared);
  } catch (err) {
    logException(err, flushLogContext(slot.item));
  }
}

async function flushPending(batch: PendingItem[]) {
  const slots = await Promise.all(batch.map(prepareFlushSlot));
  for (const slot of slots) {
    commitFlushSlot(slot);
  }
}

function enqueueFlush(run: Promise<void>, batch?: PendingItem[]) {
  if (batch) {
    kickedBatches.add(batch);
  }
  flushChain = run.then(
    () => undefined,
    () => undefined,
  );
  inFlightFlushes.add(run);
  void run.finally(() => {
    if (batch) {
      kickedBatches.delete(batch);
    }
    inFlightFlushes.delete(run);
  });
}

function kickFlush() {
  flushTimer = null;
  const batch = pending.splice(0, pending.length);
  if (batch.length === 0) {
    return;
  }

  recordCoalesceFlush(CoalesceFlushReason.TIMER, batch.length);
  enqueueFlush(
    flushChain.then(() => flushPending(batch)),
    batch,
  );
}

function agentHasKickedSession(agentId: string): boolean {
  for (const batch of kickedBatches) {
    if (
      batch.some((item) => item.kind === 'session' && item.agentId === agentId)
    ) {
      return true;
    }
  }
  return false;
}

async function drainSessionsForAgent(
  sessions: SessionItem[],
  waitedOnKick: boolean,
  timerArmed: boolean,
): Promise<void> {
  const waitStart = Date.now();
  const run = flushChain.then(async () => {
    if (waitedOnKick) {
      recordCoalesceDrainWait(Date.now() - waitStart);
    }
    if (sessions.length === 0) {
      return;
    }
    recordCoalesceFlush(CoalesceFlushReason.DRAIN, sessions.length, timerArmed);
    await flushPending(sessions);
  });
  // Same kickedBatches set as a timer kick so a sibling wrapup/away/ready in
  // this receive waits on this drain instead of applying immediately.
  enqueueFlush(run, sessions.length > 0 ? sessions : undefined);
  await run;
}

/**
 * Apply this agent's buffered session events now (after any already-kicked
 * flush finishes, which includes kicked work for this agent), then return
 * so wrapup / away / ready / external can run. Does not drop those events.
 *
 * Sessions are taken off `pending` immediately so a timer kick cannot splice
 * them onto a later chained flush while this drain waits. The drain itself is
 * tracked in `inFlightFlushes` so close/resync wait for it.
 *
 * Returns void when there is nothing to wait on so flag-off / self siblings
 * stay hop-equivalent to master. `await undefined` still yields a microtask.
 */
export function drainPendingAgentSessionEvents(
  agentId: string,
): void | Promise<void> {
  if (!isWorkspaceSessionCoalesceEnabled()) {
    return;
  }
  const timerArmed = flushTimer != null;
  const hasKicked = agentHasKickedSession(agentId);
  const sessions = takeSessionsForAgent(pending, agentId);
  if (sessions.length === 0 && !hasKicked) {
    return;
  }
  return drainSessionsForAgent(sessions, hasKicked, timerArmed);
}

function isWorkspaceSessionCoalesceEnabled() {
  return (
    featureFlagModule.isEnabled(
      FeatureFlagKeys.featureCoalesceWorkspaceEvents,
    ) &&
    featureFlagModule.isEnabled(
      FeatureFlagKeys.featureCoalesceAgentStatusUpdates,
    )
  );
}

/**
 * Flag-off / self / unknown-user: same hop count as master (`await requireUser`
 * then write in this function). `{ prepare, commit }` is only for the buffered
 * non-self flush — `await prepare()` then `commit` adds a microtask and can
 * reorder vs wrapup / away / ready in one SignalR receive.
 */
export async function applyAgentSessionNow(
  agentId: string,
  commit: (user: ServiceUser) => void,
): Promise<void> {
  if (!isValidUser(agentId)) {
    return;
  }
  const user = await requireUser(agentId);
  commit(user);
}

export function shouldBufferNonSelfAgentSession(agentId: string): boolean {
  const contactCenterUserId = serviceState.contactCenterUserId;
  return (
    isWorkspaceSessionCoalesceEnabled() &&
    contactCenterUserId != null &&
    agentId !== contactCenterUserId
  );
}

/**
 * Applies immediately when the workspace flag is off, contactCenterUserId is
 * unknown, or the event is for this agent. Otherwise buffers until the ~1s
 * flush. Session buffering requires the status flag as well.
 *
 * Immediate session handlers should use `applyAgentSessionNow`, not this
 * enqueue, so commit is not an extra await past `requireUser`.
 */
export function enqueueNonSelfAgentSessionEvent(
  agentId: string,
  apply: () => Promise<void>,
): Promise<void> | void;
export function enqueueNonSelfAgentSessionEvent<T>(
  agentId: string,
  apply: CoalescedApply<T>,
): Promise<void> | void;
export function enqueueNonSelfAgentSessionEvent<T>(
  agentId: string,
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (!shouldBufferNonSelfAgentSession(agentId)) {
    return applyImmediate(apply);
  }

  const steps = toSteps(apply);
  dropCoalescedPendingAgentStatus(agentId);
  pending.push({
    kind: 'session',
    agentId,
    ...wrapSessionApply(steps),
  });
  armFlushTimer();
}

/**
 * Shared-flusher status path (workspace flag on). Last-write-wins per agentId.
 * When the workspace flag is off, handleAgentStatus uses the CCPE-1920 Map.
 */
export function enqueueAgentStatusEvent(
  agentId: string,
  apply: () => Promise<void>,
): Promise<void> | void;
export function enqueueAgentStatusEvent<T>(
  agentId: string,
  apply: CoalescedApply<T>,
): Promise<void> | void;
export function enqueueAgentStatusEvent<T>(
  agentId: string,
  apply: (() => Promise<void>) | CoalescedApply<T>,
): Promise<void> | void {
  if (!shouldBufferNonSelfAgentSession(agentId)) {
    return applyImmediate(apply);
  }

  const steps = toSteps(apply);
  const existingIndex = pending.findIndex(
    (item): item is AgentStatusItem =>
      item.kind === 'agentStatus' && item.agentId === agentId,
  );
  if (existingIndex >= 0) {
    const existing = pending[existingIndex] as AgentStatusItem;
    Object.assign(existing, wrapStatusApply(agentId, steps));
    pending.splice(existingIndex, 1);
    pending.push(existing);
    return;
  }

  pending.push({
    kind: 'agentStatus',
    agentId,
    ...wrapStatusApply(agentId, steps),
  });
  armFlushTimer();
}
